import { collectExecutableCodeBlocks } from "@corca/tiptap";
import { autorun, makeAutoObservable, runInAction } from "mobx";

import { MarimoApi, documentMarimoToken, isApiError } from "@/api";

import type {
  MarimoCellExecutionResult,
  MarimoCellId,
  MarimoCellOpData,
  MarimoCellOutput,
  MarimoCellStatus,
  MarimoDisplayOutput,
  MarimoMessage,
  MarimoSessionStatus,
} from "./types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { ExecutableCodeBlocks } from "@corca/tiptap";

const MARIMO_METADATA_KEY = "__metadata__";
const MARIMO_EMPTY_CELL_OUTPUT_DATA = "";
const MARIMO_SESSION_STARTED_MESSAGE = "marimo-session-started";
export const MARIMO_OUTPUTS_TTL = 28 * 86400;

function waitForObservable(condition: () => boolean, rejectWhen: () => boolean) {
  return new Promise<void>((resolve, reject) => {
    const disposer = autorun(() => {
      if (condition()) {
        resolve();
        disposer();
      } else if (rejectWhen()) {
        reject(new Error("Marimo session aborted before ready"));
        disposer();
      }
    });
  });
}

export class MarimoManager {
  private doc: DocumentModel;

  private executionResults = new Map<MarimoCellId, MarimoCellExecutionResult>();

  private disposed = false;

  webSocket: WebSocket | null = null;

  sessionId: string | null = null;

  sessionStatus: MarimoSessionStatus = "disconnected";

  cellStatuses = new Map<MarimoCellId, MarimoCellStatus>();

  usageStats: MarimoApi.MarimoUsage | null = null;

  private accessToken: { token: string; expiresAt: number } | null = null;

  private accessTokenPromise: Promise<string> | null = null;

  private handleSessionStarted = ({ payload }: { payload: string }) => {
    if (payload === MARIMO_SESSION_STARTED_MESSAGE) {
      void this.attachToRunningSession();
    }
  };

  private get canAttach(): boolean {
    return !this.disposed && this.sessionStatus !== "starting" && this.sessionStatus !== "running";
  }

  constructor(doc: DocumentModel) {
    this.doc = doc;

    makeAutoObservable<
      typeof this,
      "handleCellMessage" | "handleSessionStarted" | "accessToken" | "accessTokenPromise"
    >(this, {
      handleCellMessage: false,
      handleSessionStarted: false,
      accessToken: false,
      accessTokenPromise: false,
    });

    if (doc.yProvider) {
      doc.yProvider.on("stateless", this.handleSessionStarted);
      void this.attachToRunningSession();
    }
  }

  private async attachToRunningSession(): Promise<void> {
    const { id: documentId } = this.doc;
    if (!documentId || !this.doc.editable || !this.canAttach) {
      return;
    }

    try {
      const authToken = await this.getAuthToken();
      const session = await MarimoApi.getMarimoSession(
        documentId,
        MarimoApi.getMarimoRequestConfig(authToken),
      );
      if (session.status !== "running" || !this.canAttach) {
        return;
      }

      runInAction(() => {
        this.sessionStatus = "starting";
      });
      await this.connectToSession(session, authToken);
    } catch (error) {
      if (!this.disposed) {
        console.warn("Failed to attach to running marimo session:", error);
      }
    }
  }

  async getAuthToken(): Promise<string> {
    const { id: docId } = this.doc;
    if (!docId) {
      throw new Error("Cannot fetch marimo token for an unsaved document");
    }

    const { accessToken } = this;
    if (accessToken && accessToken.expiresAt - Date.now() > 180_000) {
      return accessToken.token;
    }

    this.accessTokenPromise ??= this.fetchAuthToken(docId).finally(() => {
      this.accessTokenPromise = null;
    });
    return this.accessTokenPromise;
  }

  private async fetchAuthToken(docId: string): Promise<string> {
    try {
      const res = await documentMarimoToken(docId);
      this.accessToken = {
        token: res.token,
        expiresAt: new Date(res.expiresAt).getTime(),
      };
      return res.token;
    } catch (e) {
      console.error("Failed to fetch marimo token", e);
      throw new Error("Failed to fetch marimo token");
    }
  }

  isSessionReady() {
    return this.sessionStatus === "running";
  }

  isCellAvailable(cellId: MarimoCellId) {
    const status = this.cellStatuses.get(cellId);
    return status !== "running" && status !== "queued";
  }

  private isStaleOutputCache(cellId: MarimoCellId, cacheTimestamp: number) {
    if (cacheTimestamp <= Date.now() / 1000 - MARIMO_OUTPUTS_TTL) return true;

    const currentOutputTimestamp = this.executionResults.get(cellId)?.outputTimestamp;
    return currentOutputTimestamp && currentOutputTimestamp >= cacheTimestamp;
  }

  async loadCachedOutput(
    cellId: MarimoCellId,
    cacheTimestamp: number | null,
    signal?: AbortSignal,
  ): Promise<void> {
    const { id: docId } = this.doc;
    if (!cellId || !docId || !cacheTimestamp || this.disposed || signal?.aborted) return;
    if (this.isStaleOutputCache(cellId, cacheTimestamp)) return;

    let cachedOutput: MarimoCellOutput;
    try {
      const authToken = await this.getAuthToken();
      cachedOutput = (await MarimoApi.getCellOutput(docId, cellId, {
        ...MarimoApi.getMarimoRequestConfig(authToken),
        signal,
      })) as MarimoCellOutput;
    } catch (error) {
      if (isApiError(error) && error.response?.status === 404) return;
      console.warn(`Failed to load cached marimo output for ${cellId}`, error);
      return;
    }

    if (signal?.aborted || this.disposed) return;

    this.handleCellMessage({
      cell_id: cellId,
      status: "idle",
      output: cachedOutput,
    });
  }

  async initSession() {
    if (this.disposed) {
      throw new Error("MarimoManager is disposed");
    }

    if (!this.doc.id) {
      throw new Error("Cannot start marimo session for an unsaved document");
    }

    if (this.sessionStatus === "running") {
      return;
    }

    if (this.sessionStatus === "starting") {
      await waitForObservable(
        () => this.sessionStatus === "running",
        () =>
          this.disposed || this.sessionStatus === "error" || this.sessionStatus === "disconnected",
      );
      return;
    }

    runInAction(() => {
      this.sessionStatus = "starting";
    });

    if (this.webSocket) {
      this.webSocket.close();
      runInAction(() => {
        this.webSocket = null;
      });
    }

    let session: MarimoApi.StartMarimoSessionResult;
    let authToken: string;
    try {
      authToken = await this.getAuthToken();
      session = await MarimoApi.startMarimoSession(
        this.doc.id,
        MarimoApi.getMarimoRequestConfig(authToken),
      );
    } catch (error) {
      runInAction(() => {
        this.sessionStatus = "error";
      });
      console.error("Marimo start session api error:", error);
      throw new Error("Failed to start new marimo session");
    }

    await this.connectToSession(session, authToken);
  }

  private async connectToSession(
    session: MarimoApi.StartMarimoSessionResult,
    authToken: string,
  ): Promise<void> {
    runInAction(() => {
      this.sessionId = session.sessionId;
    });

    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(session.wsUrl, ["auth-token", authToken]);

      ws.onmessage = (event) => {
        let msg: MarimoMessage;
        try {
          msg = JSON.parse(event.data as string) as MarimoMessage;
        } catch (error) {
          console.error("Error processing marimo message:", error, event.data);
          return;
        }

        if (msg.op === "kernel-ready" || msg.op === "reconnected") {
          runInAction(() => {
            this.sessionStatus = "running";
          });
          this.attachSessionHandlers(ws);
          resolve();
        } else {
          console.warn("Unexpected marimo startup message:", msg);
        }
      };

      ws.onerror = (error) => {
        console.error("Marimo websocket error:", error);
        runInAction(() => {
          this.sessionStatus = "error";
        });
        reject(new Error("Marimo websocket errored before ready"));
      };

      ws.onclose = () => {
        runInAction(() => {
          this.sessionStatus = "disconnected";
        });
        reject(new Error("Marimo websocket closed before ready"));
      };

      runInAction(() => {
        this.webSocket = ws;
      });
    });
  }

  private attachSessionHandlers(ws: WebSocket) {
    const isActive = () => this.webSocket === ws && !this.disposed;

    ws.onmessage = (event) => {
      if (!isActive()) {
        return;
      }
      let msg: MarimoMessage;
      try {
        msg = JSON.parse(event.data as string) as MarimoMessage;
      } catch (error) {
        console.error("Error processing marimo message:", error, event.data);
        return;
      }
      if (msg.op === "cell-op") {
        this.handleCellMessage(msg.data as MarimoCellOpData);
      } else {
        console.warn("Unexpected marimo processing message:", msg);
      }
    };

    ws.onerror = (error) => {
      if (!isActive()) {
        return;
      }
      console.error("Marimo websocket error:", error);
      runInAction(() => {
        this.sessionStatus = "error";
      });
    };

    ws.onclose = () => {
      if (!isActive()) {
        return;
      }
      runInAction(() => {
        this.sessionStatus = "disconnected";
      });
    };
  }

  getCellExecutionResults(cellId: MarimoCellId) {
    return this.executionResults.get(cellId);
  }

  private handleCellMessage(cellData: MarimoCellOpData) {
    const { cell_id: cellId, status, output, console: consoleOutput } = cellData;
    if (!cellId) {
      return;
    }

    const existing: MarimoCellExecutionResult = this.executionResults.get(cellId) ?? {
      status: "idle",
      displayOutputs: [],
      consoleOutputs: [],
      stale: false,
      outputTimestamp: null,
    };

    let displayOutputs: MarimoDisplayOutput[] | null = null;
    if (output?.mimetype === "application/vnd.marimo+mimebundle") {
      const bundle = JSON.parse(output.data as string) as Record<string, unknown>;
      displayOutputs = Object.entries(bundle)
        .filter(([k, data]) => k !== MARIMO_METADATA_KEY && data !== MARIMO_EMPTY_CELL_OUTPUT_DATA)
        .map(([mimetype, data]) => ({ mimetype, data }));
    } else if (output) {
      const { mimetype, data } = output;
      displayOutputs = data !== MARIMO_EMPTY_CELL_OUTPUT_DATA ? [{ mimetype, data }] : [];
    }

    let consoleOutputs: MarimoCellOutput[] | null = null;
    if (Array.isArray(consoleOutput)) {
      consoleOutputs =
        consoleOutput.length > 0 ? [...existing.consoleOutputs, ...consoleOutput] : [];
    } else if (consoleOutput) {
      consoleOutputs = [...existing.consoleOutputs, consoleOutput];
    }

    const next: MarimoCellExecutionResult = {
      status: status ?? existing.status,
      displayOutputs: displayOutputs ?? existing.displayOutputs,
      consoleOutputs: consoleOutputs ?? existing.consoleOutputs,
      stale: false,
      outputTimestamp: output?.timestamp ?? existing.outputTimestamp,
    };

    runInAction(() => {
      if (status) {
        this.cellStatuses.set(cellId, status);
      } else {
        this.cellStatuses.set(cellId, "error");
      }
      this.saveResults(cellId, next);
    });
  }

  saveResults(cellId: MarimoCellId, result: MarimoCellExecutionResult) {
    this.executionResults.set(cellId, result);
  }

  deleteResults(cellId: MarimoCellId) {
    this.executionResults.delete(cellId);
  }

  stopSession() {
    if (!this.sessionId) {
      return;
    }

    if (this.webSocket) {
      this.webSocket.close();
      runInAction(() => {
        this.webSocket = null;
      });
    }
    runInAction(() => {
      this.sessionId = null;
      this.sessionStatus = "stopped";
    });
  }

  async stopKernel() {
    const { id: docId } = this.doc;
    if (!docId) return;

    try {
      const authToken = await this.getAuthToken();
      await MarimoApi.stopMarimoSession(docId, MarimoApi.getMarimoRequestConfig(authToken));
    } catch (error) {
      if (!(isApiError(error) && error.response?.status === 404)) {
        console.error("Failed to stop marimo kernel:", error);
      }
    } finally {
      this.stopSession();
    }
  }

  async refreshUsage() {
    const { id: docId } = this.doc;
    if (!docId || this.sessionStatus !== "running") return;

    try {
      const authToken = await this.getAuthToken();
      if (this.disposed) return;
      const updatedUsageStats = await MarimoApi.marimoUsage(
        docId,
        MarimoApi.getMarimoRequestConfig(authToken),
      );
      if (this.disposed) return;
      runInAction(() => {
        this.usageStats = updatedUsageStats as MarimoApi.MarimoUsage;
      });
    } catch (error) {
      if (!this.disposed) {
        console.warn("Failed to fetch marimo usage:", error);
      }
    }
  }

  async runCells(requestedCellIds?: MarimoCellId[]) {
    if (requestedCellIds && !requestedCellIds.length) return;

    const { tiptap } = this.doc.editor;
    if (!tiptap) return;

    const { active: snapshot, removed } = collectExecutableCodeBlocks(tiptap.state.doc);
    const requested = requestedCellIds ? new Set(requestedCellIds) : null;
    const queuedCellIds = snapshot
      .filter((node) => !requested || requested.has(node.attrs.cellId as MarimoCellId))
      .map((node) => node.attrs.cellId as MarimoCellId);
    if (!queuedCellIds.length && !removed.length) return;

    try {
      runInAction(() => {
        queuedCellIds.forEach((id) => this.cellStatuses.set(id, "queued"));
      });
      await this.initSession();
      await this.executeCode(snapshot, queuedCellIds);
    } catch (error) {
      console.error(error);
      runInAction(() => {
        queuedCellIds.forEach((id) => this.cellStatuses.set(id, "error"));
      });
    }
  }

  private async executeCode(
    snapshot: ExecutableCodeBlocks["active"],
    runIds: MarimoCellId[],
  ): Promise<void> {
    if (this.sessionStatus !== "running") {
      console.error("Marimo session is not ready", this.sessionStatus);
      return;
    }

    try {
      const authToken = await this.getAuthToken();

      runInAction(() => {
        for (const cellId of runIds) {
          if (this.executionResults.get(cellId)) {
            this.saveResults(cellId, {
              status: "queued",
              displayOutputs: [],
              consoleOutputs: [],
              stale: false,
              outputTimestamp: null,
            });
          }
          this.cellStatuses.set(cellId, "queued");
        }
      });

      await MarimoApi.marimoKernelSyncGraph(
        this.sessionId!,
        {
          cellIds: snapshot.map((n) => n.attrs.cellId as string),
          codes: snapshot.map((n) => n.textContent),
          runIds,
        },
        MarimoApi.getMarimoRequestConfig(authToken),
      );
    } catch (error) {
      console.error("Error executing marimo cells:", error);
      runInAction(() => {
        for (const cellId of runIds) {
          this.cellStatuses.set(cellId, "error");
        }
      });
    }
  }

  dispose() {
    this.doc.yProvider?.off("stateless", this.handleSessionStarted);
    this.disposed = true;
    this.usageStats = null;
    this.stopSession();
    this.cellStatuses.clear();
    this.executionResults.clear();
  }
}
